import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { SubscriptionStatusBadge } from "@/components/ui/StatusBadge";
import { LoadingState, ErrorState } from "@/components/ui/AsyncState";
import { useApi } from "@/lib/use-api";
import { formatCurrencyFromCents, formatDateTime, formatPhone } from "@/lib/format";
import { planDisplayName } from "@platform/shared";
import type {
  AdminCustomerBilling,
  AdminCustomerDetail,
  AdminCustomerRecentLead,
  AdminCustomerRecentCall,
  AdminCustomerRecentConversation,
  LeadStatus,
} from "@platform/shared";

interface CustomerDetailResponse {
  customer: AdminCustomerDetail;
  recentLeads: AdminCustomerRecentLead[];
  recentCalls: AdminCustomerRecentCall[];
  recentConversations: AdminCustomerRecentConversation[];
  billing: AdminCustomerBilling;
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 border-b border-line py-2.5 text-sm last:border-0">
      <span className="text-ink-soft">{label}</span>
      <span className="text-right font-medium text-ink">{value}</span>
    </div>
  );
}

const LEAD_STATUS_LABEL: Record<LeadStatus, string> = {
  new: "New",
  contacted: "Contacted",
  qualified: "Qualified",
  won: "Won",
  lost: "Lost",
};

export function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error, refetch } = useApi<CustomerDetailResponse>(
    id ? `/api/admin/customers/${id}` : null,
    [id]
  );

  if (isLoading) return <LoadingState label="Loading customer…" />;
  if (error) return <ErrorState message={error} onRetry={refetch} />;
  if (!data) return null;

  const { customer, recentLeads, recentCalls, recentConversations, billing } = data;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin/customers" className="text-sm text-ink-soft hover:text-ink">
          ← Back to customers
        </Link>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl text-ink">{customer.company}</h1>
            <p className="mt-1 text-sm text-ink-soft">{customer.slug}</p>
          </div>
          <Badge tone={customer.isActive ? "success" : "signal"}>
            {customer.isActive ? "Active" : "Suspended"}
          </Badge>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.2fr,1fr]">
        <div className="space-y-6">
          <Card className="p-5">
            <p className="mb-1 font-medium text-ink">Account details</p>
            <DetailRow label="Customer since" value={formatDateTime(customer.createdAt)} />
            <DetailRow
              label="Last activity"
              value={customer.lastActivityAt ? formatDateTime(customer.lastActivityAt) : "No activity yet"}
            />
            <DetailRow label="All-time revenue" value={formatCurrencyFromCents(customer.totalRevenueCents)} />
          </Card>

          <Card className="p-5">
            <div className="mb-1 flex items-center justify-between">
              <p className="font-medium text-ink">Billing</p>
              {billing.status && <SubscriptionStatusBadge status={billing.status} />}
            </div>
            <DetailRow label="Plan" value={billing.plan ? planDisplayName(billing.plan) : "—"} />
            <DetailRow
              label={billing.cancelAtPeriodEnd ? "Access ends" : "Current period ends"}
              value={billing.currentPeriodEnd ? formatDateTime(billing.currentPeriodEnd) : "—"}
            />
            <DetailRow label="Cancels at period end" value={billing.cancelAtPeriodEnd ? "Yes" : "No"} />
            <DetailRow
              label="Stripe customer"
              value={billing.stripeCustomerId ? <code className="text-xs">{billing.stripeCustomerId}</code> : "—"}
            />
            <DetailRow
              label="Stripe subscription"
              value={billing.stripeSubscriptionId ? <code className="text-xs">{billing.stripeSubscriptionId}</code> : "—"}
            />
            <DetailRow
              label="Stripe price"
              value={billing.stripePriceId ? <code className="text-xs">{billing.stripePriceId}</code> : "—"}
            />

            <p className="mb-1 mt-5 text-sm font-medium text-ink">Recent payments</p>
            {billing.recentInvoices.length === 0 ? (
              <p className="py-2 text-sm text-ink-soft">No Stripe payments recorded yet.</p>
            ) : (
              billing.recentInvoices.map((invoice) => (
                <DetailRow
                  key={invoice.stripeInvoiceId}
                  label={`${invoice.occurredOn} · ${invoice.stripeInvoiceId}`}
                  value={formatCurrencyFromCents(invoice.amountCents)}
                />
              ))
            )}

            <p className="mb-1 mt-5 text-sm font-medium text-ink">Recent Stripe events</p>
            {billing.recentWebhookEvents.length === 0 ? (
              <p className="py-2 text-sm text-ink-soft">No Stripe events received for this customer.</p>
            ) : (
              billing.recentWebhookEvents.map((event) => (
                <DetailRow
                  key={event.id}
                  label={`${formatDateTime(event.createdAt)} · ${event.eventType}`}
                  value={<Badge tone={event.status === "error" ? "signal" : "neutral"}>{event.status}</Badge>}
                />
              ))
            )}
          </Card>

          <Card className="p-5">
            <p className="mb-1 font-medium text-ink">Activity</p>
            <DetailRow label="Total leads" value={String(customer.leadsTotal)} />
            {Object.entries(customer.leadsByStatus).map(([status, count]) => (
              <DetailRow
                key={status}
                label={`  ${LEAD_STATUS_LABEL[status as LeadStatus] ?? status}`}
                value={String(count)}
              />
            ))}
            <DetailRow label="Total calls" value={String(customer.callsTotal)} />
            <DetailRow label="Total conversations" value={String(customer.conversationsTotal)} />
          </Card>

          <Card className="overflow-hidden">
            <div className="border-b border-line px-5 py-3">
              <p className="font-medium text-ink">Team</p>
            </div>
            {customer.members.length === 0 ? (
              <p className="px-5 py-4 text-sm text-ink-soft">No members.</p>
            ) : (
              <ul className="divide-y divide-line">
                {customer.members.map((member) => (
                  <li key={member.userId} className="flex items-center justify-between px-5 py-3 text-sm">
                    <div>
                      <p className="font-medium text-ink">{member.fullName}</p>
                      <p className="text-xs text-ink-soft">{member.email}</p>
                    </div>
                    <Badge tone="neutral">{member.role}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="overflow-hidden">
            <div className="border-b border-line px-5 py-3">
              <p className="font-medium text-ink">Recent leads</p>
            </div>
            {recentLeads.length === 0 ? (
              <p className="px-5 py-4 text-sm text-ink-soft">No leads yet.</p>
            ) : (
              <ul className="divide-y divide-line">
                {recentLeads.map((lead) => (
                  <li key={lead.id} className="flex items-center justify-between px-5 py-3 text-sm">
                    <span className="text-ink">{lead.name ?? formatPhone(lead.phone)}</span>
                    <Badge tone="neutral">{LEAD_STATUS_LABEL[lead.status] ?? lead.status}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="overflow-hidden">
            <div className="border-b border-line px-5 py-3">
              <p className="font-medium text-ink">Recent calls</p>
            </div>
            {recentCalls.length === 0 ? (
              <p className="px-5 py-4 text-sm text-ink-soft">No calls yet.</p>
            ) : (
              <ul className="divide-y divide-line">
                {recentCalls.map((call) => (
                  <li key={call.id} className="flex items-center justify-between px-5 py-3 text-sm">
                    <span className="text-ink-soft">{formatDateTime(call.occurredAt)}</span>
                    <Badge tone="neutral">{call.outcome}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="overflow-hidden">
            <div className="border-b border-line px-5 py-3">
              <p className="font-medium text-ink">Recent conversations</p>
            </div>
            {recentConversations.length === 0 ? (
              <p className="px-5 py-4 text-sm text-ink-soft">No conversations yet.</p>
            ) : (
              <ul className="divide-y divide-line">
                {recentConversations.map((conv) => (
                  <li key={conv.id} className="flex items-center justify-between px-5 py-3 text-sm">
                    <span className="text-ink-soft">{formatDateTime(conv.updatedAt)}</span>
                    <Badge tone="neutral">{conv.status}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
